import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import denvoraLogo from "@/assets/denvora-mobility-logo.png";
import { getAriaLabel, handleKeyboardNavigation, focusManagement } from "@/utils/accessibility";
import { PRODUCT_BRAND } from "@/config/platform";
import {
  getDestinationNavItems,
  getDestinationsMegaMenuQuickLinks,
  getPortugalServiceNavItems,
  getPortugalVisaNavItems,
} from "@/utils/destinationNav";
import { CountryFlag } from "@/components/CountryFlag";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false);
  const { t, language } = useLanguage();
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setIsDestinationsOpen(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen && menuRef.current) {
      const cleanup = focusManagement.trapFocus(menuRef.current);
      return cleanup;
    }
  }, [isMenuOpen]);

  const lang = language === 'en' ? 'en' : 'pt';
  const destinationNavItems = getDestinationNavItems(lang);
  const portugalServiceItems = getPortugalServiceNavItems(lang);
  const visaItems = getPortugalVisaNavItems(lang);
  const megaMenuQuickLinks = getDestinationsMegaMenuQuickLinks(lang);

  const mainMenuItems = [
    { key: 'nav.home', route: '/' },
    { key: 'nav.about', route: language === 'en' ? '/about-us' : '/sobre-nos' },
  ];

  const contactRoute = language === 'en' ? '/contact' : '/contato';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[hsl(0,0%,100%)] dark:bg-[hsl(210,40%,5%)] border-b border-border shadow-sm" role="banner">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" aria-label={language === 'en' ? 'Main navigation' : 'Navegação principal'}>
        <div className="flex justify-between items-center h-20 lg:h-24">
          <div className="flex-shrink-0">
            <Link 
              to="/"
              aria-label={language === 'en' ? `${PRODUCT_BRAND} Home` : `${PRODUCT_BRAND} Início`}
            >
              <img 
                src={denvoraLogo} 
                alt={language === 'en' 
                  ? `${PRODUCT_BRAND} - International residency intelligence across Portugal, Spain, Italy, and Greece` 
                  : `${PRODUCT_BRAND} - Inteligência de residência internacional em Portugal, Espanha, Itália e Grécia`}
                className="h-14 w-auto md:h-[4.25rem] lg:h-[4.75rem] object-contain"
                width="240"
                height="76"
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
              <span className="sr-only">{language === 'en' ? `${PRODUCT_BRAND} Home` : `${PRODUCT_BRAND} Início`}</span>
            </Link>
          </div>

          {/* Desktop & tablet navigation */}
          <div className="hidden md:flex items-center space-x-1 xl:space-x-2">
            <NavigationMenu>
              <NavigationMenuList className="flex items-baseline space-x-1 xl:space-x-1.5">
                {mainMenuItems.map((item) => (
                  <NavigationMenuItem key={item.key}>
                    <Link 
                      to={item.route} 
                      className="text-foreground hover:text-accent transition-all duration-300 text-sm xl:text-base font-medium whitespace-nowrap relative group px-1.5 xl:px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t(item.key)}
                    >
                      <span className="relative z-10">{t(item.key)}</span>
                      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-accent transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                    </Link>
                  </NavigationMenuItem>
                ))}

                {/* Destinations mega-menu: countries + Portugal visas & services */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="text-foreground hover:text-accent transition-all duration-300 text-sm xl:text-base font-medium whitespace-nowrap px-1.5 xl:px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label={language === 'en' ? 'Destinations menu' : 'Menu Destinos'}
                  >
                    {t('nav.destinations')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[720px] max-w-[95vw] p-4 space-y-4">
                      <div className="grid gap-3 md:grid-cols-2">
                        {destinationNavItems.map((dest) => (
                          <Link
                            key={dest.id}
                            to={dest.href}
                            className={`group select-none space-y-1 rounded-md p-3 min-h-[48px] flex flex-col justify-center leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-white focus:bg-accent focus:text-white ${dest.highlighted ? 'bg-accent/10 border border-accent/30' : ''}`}
                          >
                            <div className="flex items-center gap-2.5 mb-1">
                              <CountryFlag
                                iso={dest.flag}
                                alt={t(dest.nameKey)}
                                className="h-6 w-9 rounded-sm object-cover border border-border/40 shadow-sm shrink-0 group-hover:border-white/30"
                              />
                              <div className={`text-sm font-medium ${dest.highlighted ? 'text-accent font-semibold' : 'text-primary'} group-hover:text-white`}>
                                {t(dest.nameKey)}
                              </div>
                            </div>
                            <p className="line-clamp-2 text-sm text-muted-foreground group-hover:text-white/95 pl-[2.625rem]">
                              {t(dest.descriptionKey)}
                            </p>
                          </Link>
                        ))}
                      </div>

                      <div className="border-t pt-3 space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 flex items-center gap-2">
                          <CountryFlag iso="PT" alt="Portugal" className="h-4 w-6 rounded-sm object-cover border border-border/40 shrink-0" />
                          {t('nav.portugalVisas')}
                        </p>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                          {visaItems.map((visa) => (
                            <Link
                              key={visa.key}
                              to={visa.route}
                              className={`rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${visa.highlighted ? 'bg-accent/10 font-semibold text-accent' : 'text-primary'}`}
                              aria-label={`${getAriaLabel(language, 'navigateTo')} ${visa.key}: ${visa.description}`}
                            >
                              <span className="block font-medium leading-tight">{visa.key}</span>
                              <span className="block text-xs text-muted-foreground line-clamp-1 mt-0.5">{visa.description}</span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="border-t pt-3 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 flex items-center gap-2">
                          <CountryFlag iso="PT" alt="Portugal" className="h-4 w-6 rounded-sm object-cover border border-border/40 shrink-0" />
                          {t('nav.portugalServices')}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {portugalServiceItems.map((item) => (
                            <Link
                              key={item.key}
                              to={item.route}
                              className="text-sm px-2 py-2 rounded-md hover:bg-accent/10 text-primary"
                            >
                              {t(item.key)}
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="border-t pt-3 flex flex-wrap gap-2">
                        {megaMenuQuickLinks.map((link) => (
                          <Link
                            key={link.key}
                            to={link.route}
                            className="text-sm font-medium px-3 py-2 rounded-md border border-accent/30 text-accent hover:bg-accent hover:text-white transition-colors"
                          >
                            {t(link.key)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/interactive"
                    className="text-foreground hover:text-accent transition-all duration-300 text-sm xl:text-base font-medium whitespace-nowrap relative group px-1.5 xl:px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.toolkit')}
                  >
                    <span className="relative z-10">{t('nav.toolkit')}</span>
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-accent transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/blog"
                    className="inline-flex items-center justify-center min-h-[44px] px-2 xl:px-3 py-2 rounded-md text-sm xl:text-base font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    style={{
                      backgroundColor: 'hsla(45, 85%, 60%, 0.1)',
                      color: 'hsl(45, 85%, 60%)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'hsl(45, 85%, 60%)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'hsla(45, 85%, 60%, 0.1)';
                      e.currentTarget.style.color = 'hsl(45, 85%, 60%)';
                    }}
                    aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.blog')}
                  >
                    {t('nav.blog')}
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/parceiros"
                    className="inline-flex items-center justify-center min-h-[44px] px-2 xl:px-3 py-2 rounded-md text-sm xl:text-base font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    style={{
                      border: '2px solid hsl(45, 85%, 60%)',
                      color: 'hsl(45, 85%, 60%)',
                      backgroundColor: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'hsl(45, 85%, 60%)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'hsl(45, 85%, 60%)';
                    }}
                    aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.partners')}
                  >
                    {t('nav.partners')}
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to={contactRoute}
                    className="inline-flex items-center justify-center min-h-[44px] px-3 xl:px-4 py-2 rounded-md text-sm xl:text-base font-semibold whitespace-nowrap shadow-md transition-all duration-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    style={{
                      backgroundColor: 'hsl(43, 74%, 36%)',
                      color: 'white',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'hsl(43, 74%, 32%)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'hsl(43, 74%, 36%)';
                    }}
                    aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.contact')}
                  >
                    {t('nav.contact')}
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <LanguageSelector />
          </div>

          <div className="md:hidden flex items-center space-x-2">
            <LanguageSelector />
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              onKeyDown={(e) => handleKeyboardNavigation(e, toggleMenu, { keys: ['Enter', ' '] })}
              className="text-foreground hover:text-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:text-accent p-2.5 min-h-[48px] min-w-[48px] rounded transition-colors inline-flex items-center justify-center"
              aria-label={isMenuOpen ? getAriaLabel(language, 'closeMenu') : getAriaLabel(language, 'openMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div 
            id="mobile-menu"
            ref={menuRef}
            className="md:hidden absolute left-0 right-0 top-full bg-background border-t border-border shadow-lg z-50"
            role="dialog"
            aria-modal="true"
            aria-label={language === 'en' ? 'Mobile navigation menu' : 'Menu de navegação móvel'}
          >
            <div className="px-2 pt-2 pb-6 space-y-1 sm:px-3 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {mainMenuItems.map((item) => (
                <Link 
                  key={item.key}
                  to={item.route} 
                  className="block px-3 py-3 min-h-[48px] text-base font-medium text-foreground hover:text-accent hover:bg-accent/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded flex items-center"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t(item.key)}
                >
                  {t(item.key)}
                </Link>
              ))}

              <Link 
                to="/interactive"
                className="block px-3 py-3 min-h-[48px] text-base font-medium text-foreground hover:text-accent hover:bg-accent/10 transition-colors rounded flex items-center"
                onClick={() => setIsMenuOpen(false)}
                aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.toolkit')}
              >
                {t('nav.toolkit')}
              </Link>
              
              <div className="pt-2 pb-1" role="group" aria-label={language === 'en' ? 'Destinations' : 'Destinos'}>
                <button
                  onClick={() => setIsDestinationsOpen(!isDestinationsOpen)}
                  className="w-full flex items-center justify-between px-3 py-3 min-h-[48px] text-base font-semibold text-primary hover:text-accent hover:bg-accent/10 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 bg-accent/5"
                  aria-expanded={isDestinationsOpen}
                  aria-controls="mobile-destinations-list"
                >
                  <span>{t('nav.destinations')}</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-200 ${isDestinationsOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {isDestinationsOpen && (
                  <div id="mobile-destinations-list" className="mt-1 space-y-1">
                    {destinationNavItems.map((dest) => (
                      <Link
                        key={dest.id}
                        to={dest.href}
                        className={`flex items-center gap-3 px-6 py-3 min-h-[48px] text-sm hover:bg-accent/5 rounded ${dest.highlighted ? 'border-l-4 border-accent bg-accent/10 font-semibold' : ''}`}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsDestinationsOpen(false);
                        }}
                      >
                        <CountryFlag iso={dest.flag} alt={t(dest.nameKey)} className="h-5 w-8 rounded-sm object-cover border border-border/40 shrink-0" />
                        {t(dest.nameKey)}
                      </Link>
                    ))}

                    <p className="px-6 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <CountryFlag iso="PT" alt="Portugal" className="h-4 w-6 rounded-sm object-cover border border-border/40 shrink-0" />
                      {t('nav.portugalVisas')}
                    </p>
                    {visaItems.map((visa) => (
                      <Link 
                        key={visa.key}
                        to={visa.route} 
                        className={`block px-8 py-2.5 min-h-[44px] text-sm text-foreground hover:text-accent hover:bg-accent/5 transition-colors rounded flex flex-col justify-center ${visa.highlighted ? 'bg-accent/10 border-l-4 border-accent font-semibold' : ''}`}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsDestinationsOpen(false);
                        }}
                        aria-label={`${getAriaLabel(language, 'navigateTo')} ${visa.key}: ${visa.description}`}
                      >
                        <span className={`font-medium ${visa.highlighted ? 'text-accent' : ''}`}>{visa.key}</span>
                        <span className="text-xs text-muted-foreground">{visa.description}</span>
                      </Link>
                    ))}

                    <p className="px-6 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <CountryFlag iso="PT" alt="Portugal" className="h-4 w-6 rounded-sm object-cover border border-border/40 shrink-0" />
                      {t('nav.portugalServices')}
                    </p>
                    {portugalServiceItems.map((item) => (
                      <Link
                        key={item.key}
                        to={item.route}
                        className="block px-8 py-2 text-sm text-muted-foreground hover:text-accent"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsDestinationsOpen(false);
                        }}
                      >
                        {t(item.key)}
                      </Link>
                    ))}

                    <div className="px-6 pt-3 pb-2 flex flex-wrap gap-2">
                      {megaMenuQuickLinks.map((link) => (
                        <Link
                          key={link.key}
                          to={link.route}
                          className="text-sm font-medium px-3 py-2 rounded-md border border-accent/30 text-accent"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsDestinationsOpen(false);
                          }}
                        >
                          {t(link.key)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-border my-2" />

              <div className="space-y-1">
                <Link 
                  to="/blog"
                  className="block px-3 py-3 min-h-[48px] text-base font-semibold text-accent hover:bg-accent/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded bg-accent/5 flex items-center"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.blog')}
                >
                  {t('nav.blog')}
                </Link>
                
                <Link 
                  to="/parceiros"
                  className="block px-3 py-3 min-h-[48px] text-base font-semibold text-accent hover:bg-accent/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded border border-accent/30 flex items-center"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.partners')}
                >
                  {t('nav.partners')}
                </Link>
                
                <Link 
                  to={contactRoute}
                  className="block px-3 py-3 min-h-[48px] text-base font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded shadow-md text-center flex items-center justify-center"
                  style={{
                    backgroundColor: 'hsl(43, 74%, 36%)',
                    color: 'white',
                  }}
                  onClick={() => setIsMenuOpen(false)}
                  aria-label={getAriaLabel(language, 'navigateTo') + ' ' + t('nav.contact')}
                >
                  {t('nav.contact')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
